import type { ApiResponse } from '@wanjianshi/shared-contracts';
import { App, CreateButton, Form, Modal, Select, Space } from '@wanjianshi/admin-ui';
import { useCallback, useEffect, useState } from 'react';
import { AdminRemoteSelect } from '@/components/AdminRemoteSelect';
import { EntityFormModal } from '@/components/EntityFormModal';
import { MembershipEditorModal } from '@/features/memberships/MembershipEditorModal';
import { ManualRechargeModal } from '@/features/recharge/ManualRechargeModal';
import { useAdminAuth } from '@/auth/store';
import { adminApi, adminOperationOptions } from '@/services/api/client';
import { loadAdminRoleOptions, type AdminRoleOption } from '@/features/admin-users/roleOptions';
import { endpointBase, type ResourceDefinition } from './definitions';

interface AdminUserCandidate {
  id: string | number;
  nickname?: string | null;
  username?: string | null;
  mobile_masked?: string | null;
}

function AddAdminUserButton({ onReload }: { onReload: () => Promise<void> }) {
  const allowed = useAdminAuth(state => state.hasPermission('wjs-admin:admin-user:assign-role'));
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState<AdminRoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [form] = Form.useForm();
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setRolesLoading(true);
    void loadAdminRoleOptions(controller.signal)
      .then(setRoles)
      .catch(error => {
        if (!controller.signal.aborted) {
          message.error(error instanceof Error ? error.message : '后台角色加载失败');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRolesLoading(false);
      });
    return () => controller.abort();
  }, [message, open]);
  const loadUsers = useCallback(async (keyword: string, signal: AbortSignal) => {
    const response = await adminApi.get<ApiResponse<{ items: AdminUserCandidate[] }>>(
      '/admin/admin-users/user-options',
      { params: { keyword, limit: 20 }, signal },
    );
    return response.data.items ?? [];
  }, []);
  if (!allowed) return null;
  return (
    <>
      <CreateButton onClick={() => setOpen(true)}>添加后台管理员</CreateButton>
      <Modal open={open} title="从已有 system_users 添加后台管理员" okText="添加并分配角色" onCancel={() => setOpen(false)} onOk={async () => {
        const values = await form.validateFields();
        const reason = String(values.reason).trim();
        await adminApi.post(`/admin/admin-users/${values.user_id}/roles`, { role_ids: values.role_ids }, adminOperationOptions(reason));
        message.success('后台管理员已添加，用户端身份与团队权限未改变');
        setOpen(false);
        form.resetFields();
        await onReload();
      }}>
        <Form form={form} layout="vertical">
          <Form.Item name="user_id" label="选择已有用户" rules={[{ required: true }]}>
            <AdminRemoteSelect<AdminUserCandidate>
              loadOptions={loadUsers}
              mapOption={user => ({
                value: String(user.id),
                label: `${user.nickname || user.username || `用户 ${String(user.id)}`} · ${user.mobile_masked || `ID ${String(user.id)}`}`,
              })}
              placeholder="按用户名、昵称、手机或 ID 搜索"
            />
          </Form.Item>
          <Form.Item name="role_ids" label="后台角色" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={roles}
              loading={rolesLoading}
              placeholder="选择一个或多个后台角色"
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item name="reason" label="操作原因" rules={[{ required: true }]}>
            <Select options={[{ label: '新增后台运营人员', value: '新增后台运营人员' }, { label: '新增后台客服人员', value: '新增后台客服人员' }, { label: '岗位权限调整', value: '岗位权限调整' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export function ResourcePageExtra({
  endpoint,
  definition,
  onReload,
}: {
  endpoint: string;
  definition?: ResourceDefinition;
  onReload: () => Promise<void>;
}) {
  const base = endpointBase(endpoint);
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const { message } = App.useApp();
  const create = definition?.create;
  return (
    <Space wrap>
      {base === '/admin/personal-memberships' ? (
        <MembershipEditorModal kind="personal" mode="create" onSaved={onReload} />
      ) : null}
      {base === '/admin/team-memberships' ? (
        <MembershipEditorModal kind="team" mode="create" onSaved={onReload} />
      ) : null}
      {create && hasPermission(create.permission) ? (
        <EntityFormModal
          title={create.title}
          buttonLabel={create.buttonLabel}
          fields={create.fields}
          reasonRequired={create.reasonRequired}
          buttonAction="create"
          buttonProps={{ type: 'primary' }}
          onSubmit={async (values, reason) => {
            const path = typeof create.path === 'function' ? create.path({}) : create.path;
            if (create.method === 'post') await adminApi.post(path, values, adminOperationOptions(reason));
            else await adminApi.put(path, values, adminOperationOptions(reason));
            message.success('保存成功');
            await onReload();
          }}
        />
      ) : null}
      {base === '/admin/admin-users' ? <AddAdminUserButton onReload={onReload} /> : null}
      {base === '/admin/redeem-codes' && hasPermission('wjs-admin:redeem-code:export') ? (
        <EntityFormModal title="导出兑换码" buttonLabel="导出 CSV" buttonAction="download" fields={[]} reasonRequired onSubmit={async (_values, reason) => {
          const blob = await adminApi.post<Blob>('/admin/redeem-codes/export', {}, { ...adminOperationOptions(reason), responseType: 'blob' });
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url; anchor.download = `redeem-codes-${Date.now()}.csv`; anchor.click();
          URL.revokeObjectURL(url);
        }} />
      ) : null}
      {base === '/admin/recharge' && hasPermission('wjs-admin:recharge:manual-adjust') ? (
        <ManualRechargeModal onSaved={onReload} />
      ) : null}
    </Space>
  );
}
